import {
  SolContent,
  SolContentBlock,
  SolContentCodeBlock,
  SolContentDividerBlock,
  SolContentEmphasisInline,
  SolContentHeadingBlock,
  SolContentImageBlock,
  SolContentImageInline,
  SolContentInline,
  SolContentLinkInline,
  SolContentLinkTarget,
  SolContentListBlock,
  SolContentListItem,
  SolContentParagraphBlock,
  SolContentQuoteBlock,
  SolContentStrongInline,
  SolContentTableBlock,
  SolContentTextInline,
} from '../models/core/sol-content';
import { SolImage } from '../models/core/sol-image';

export interface SolMarkdownFeatures {
  readonly headings: boolean;
  readonly dividers: boolean;
  readonly tables: boolean;
  readonly lists: boolean;
  readonly quotes: boolean;
  readonly codeBlocks: boolean;
  readonly images: boolean;
  readonly bold: boolean;
  readonly emphasis: boolean;
  readonly inlineCode: boolean;
  readonly links: boolean;
}

export interface SolContentMarkdownOptions {
  readonly assetBasePath?: string;
  readonly linkResolver?: (target: string) => SolContentLinkTarget | undefined;
  readonly imageResolver?: (target: string, alt: string, title?: string) => SolImage | undefined;
  readonly features?: Partial<SolMarkdownFeatures>;
}

interface BlockParseResult<T extends SolContentBlock> {
  readonly block: T;
  readonly nextIndex: number;
}

interface ListParseResult {
  readonly block: SolContentListBlock;
  readonly nextIndex: number;
}

interface ListLine {
  readonly indent: number;
  readonly ordered: boolean;
  readonly content: string;
}

interface LinkTargetParts {
  readonly path: string;
  readonly fragment: string | undefined;
}

export class SolContentMarkdownTools {
  /** Reports parse at most tens of distinct contents, so 64 LRU entries bounds memory while covering every slice and measurement copy. */
  public static readonly parseCacheCapacity = 64;

  public static readonly ALL_FEATURES: SolMarkdownFeatures = {
    headings: true,
    dividers: true,
    tables: true,
    lists: true,
    quotes: true,
    codeBlocks: true,
    images: true,
    bold: true,
    emphasis: true,
    inlineCode: true,
    links: true,
  };

  private static readonly NO_FEATURES: SolMarkdownFeatures = {
    headings: false,
    dividers: false,
    tables: false,
    lists: false,
    quotes: false,
    codeBlocks: false,
    images: false,
    bold: false,
    emphasis: false,
    inlineCode: false,
    links: false,
  };

  private static readonly parseCache = new Map<string, SolContent>();

  /**
   * Parses markdown into content blocks. Results are cached per content string
   * (and asset base path), so identical input returns the same `SolContent`
   * reference; treat the parsed blocks as immutable. Resolver options bypass
   * the cache because their output cannot be keyed.
   */
  public static parse(markdown: string, options: SolContentMarkdownOptions = {}): SolContent {
    if (options.linkResolver !== undefined || options.imageResolver !== undefined) {
      return SolContentMarkdownTools.parseContent(markdown, options);
    }

    const featuresKey = options.features === undefined ? '' : SolContentMarkdownTools.encodeFeatures(options.features);
    const key = `${options.assetBasePath ?? ''}\u0000${featuresKey}\u0000${markdown}`;
    const cached = SolContentMarkdownTools.parseCache.get(key);
    if (cached !== undefined) {
      SolContentMarkdownTools.parseCache.delete(key);
      SolContentMarkdownTools.parseCache.set(key, cached);
      return cached;
    }

    const content = SolContentMarkdownTools.parseContent(markdown, options);
    SolContentMarkdownTools.parseCache.set(key, content);
    if (SolContentMarkdownTools.parseCache.size > SolContentMarkdownTools.parseCacheCapacity) {
      SolContentMarkdownTools.parseCache.delete(SolContentMarkdownTools.parseCache.keys().next().value!);
    }
    return content;
  }

  private static resolveFeatures(features?: Partial<SolMarkdownFeatures>): SolMarkdownFeatures {
    if (features === undefined) {
      return SolContentMarkdownTools.ALL_FEATURES;
    }
    return { ...SolContentMarkdownTools.NO_FEATURES, ...features };
  }

  private static encodeFeatures(features: Partial<SolMarkdownFeatures>): string {
    const resolved = SolContentMarkdownTools.resolveFeatures(features);
    return [
      resolved.headings,
      resolved.dividers,
      resolved.tables,
      resolved.lists,
      resolved.quotes,
      resolved.codeBlocks,
      resolved.images,
      resolved.bold,
      resolved.emphasis,
      resolved.inlineCode,
      resolved.links,
    ]
      .map(enabled => (enabled ? '1' : '0'))
      .join('');
  }

  public static toAnchorId(text: string): string {
    return text
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .trim()
      .replace(/\s/g, '-');
  }

  private static parseContent(markdown: string, options: SolContentMarkdownOptions): SolContent {
    const lines = markdown.replace(/\r\n?/g, '\n').split('\n');
    const nextId = SolContentMarkdownTools.createIdGenerator();
    const features = SolContentMarkdownTools.resolveFeatures(options.features);
    const blocks: SolContentBlock[] = [];

    let index = 0;
    while (index < lines.length) {
      const line = lines[index];
      if (line.trim() === '') {
        index++;
        continue;
      }

      if (features.codeBlocks && SolContentMarkdownTools.isFencedCodeBlockStart(line)) {
        const result = SolContentMarkdownTools.parseCodeBlock(lines, index, nextId);
        blocks.push(result.block);
        index = result.nextIndex;
        continue;
      }

      if (features.headings && SolContentMarkdownTools.isHeadingLine(line)) {
        blocks.push(SolContentMarkdownTools.parseHeading(line, nextId, options, features));
        index++;
        continue;
      }

      if (features.dividers && SolContentMarkdownTools.isDividerLine(line)) {
        blocks.push({
          id: nextId('divider'),
          type: 'divider',
        } satisfies SolContentDividerBlock);
        index++;
        continue;
      }

      if (features.tables && SolContentMarkdownTools.isTableStart(lines, index)) {
        const result = SolContentMarkdownTools.parseTable(lines, index, nextId, options);
        blocks.push(result.block);
        index = result.nextIndex;
        continue;
      }

      if (features.lists && SolContentMarkdownTools.parseListLine(line) !== undefined) {
        const result = SolContentMarkdownTools.parseList(lines, index, nextId, options, features);
        blocks.push(result.block);
        index = result.nextIndex;
        continue;
      }

      if (features.quotes && SolContentMarkdownTools.isQuoteLine(line)) {
        const result = SolContentMarkdownTools.parseQuote(lines, index, nextId, options, features);
        blocks.push(result.block);
        index = result.nextIndex;
        continue;
      }

      const result = SolContentMarkdownTools.parseParagraph(lines, index, nextId, options, features);
      blocks.push(result.block);
      index = result.nextIndex;
    }

    return SolContent.of(blocks);
  }

  private static parseCodeBlock(
    lines: string[],
    startIndex: number,
    nextId: (prefix: string) => string,
  ): BlockParseResult<SolContentCodeBlock> {
    const startLine = lines[startIndex].trim();
    const language = startLine.slice(3).trim() || undefined;
    const codeLines: string[] = [];

    let index = startIndex + 1;
    while (index < lines.length && !lines[index].trim().startsWith('```')) {
      codeLines.push(lines[index]);
      index++;
    }

    if (index < lines.length) {
      index++;
    }

    return {
      block: {
        id: nextId('code-block'),
        type: 'code',
        code: codeLines.join('\n'),
        language,
      },
      nextIndex: index,
    };
  }

  private static parseHeading(
    line: string,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
    features: SolMarkdownFeatures,
  ): SolContentHeadingBlock {
    const trimmedLine = line.trim();
    const level = trimmedLine.match(/^#+/)![0].length as 1 | 2 | 3 | 4 | 5 | 6;
    const text = trimmedLine.slice(level).trim();
    return {
      id: nextId('heading'),
      type: 'heading',
      level,
      anchorId: SolContentMarkdownTools.toAnchorId(text),
      segments: SolContentMarkdownTools.parseInline(text, nextId, options, features),
      text,
    };
  }

  private static parseTable(
    lines: string[],
    startIndex: number,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
  ): BlockParseResult<SolContentTableBlock> {
    const columns = SolContentMarkdownTools.parseTableRow(lines[startIndex]);
    const rows: string[][] = [];

    let index = startIndex + 2;
    while (index < lines.length) {
      const line = lines[index];
      if (line.trim() === '' || !line.includes('|')) {
        break;
      }
      rows.push(SolContentMarkdownTools.parseTableRow(line));
      index++;
    }

    return {
      block: {
        id: nextId('table'),
        type: 'table',
        assetBasePath: options.assetBasePath,
        columns,
        rows,
      },
      nextIndex: index,
    };
  }

  private static parseList(
    lines: string[],
    startIndex: number,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
    features: SolMarkdownFeatures,
  ): ListParseResult {
    const firstLine = SolContentMarkdownTools.parseListLine(lines[startIndex]);
    if (firstLine === undefined) {
      throw new Error('Expected markdown list line.');
    }

    const items: SolContentListItem[] = [];
    let index = startIndex;

    while (index < lines.length) {
      const listLine = SolContentMarkdownTools.parseListLine(lines[index]);
      if (listLine === undefined || listLine.indent !== firstLine.indent || listLine.ordered !== firstLine.ordered) {
        break;
      }

      index++;
      const itemLines: string[] = [listLine.content];
      let childList: SolContentListBlock | undefined;

      while (index < lines.length) {
        const currentLine = lines[index];
        if (currentLine.trim() === '') {
          index++;
          if (SolContentMarkdownTools.parseListLine(lines[index] ?? '')?.indent === listLine.indent) {
            break;
          }
          continue;
        }

        const currentListLine = SolContentMarkdownTools.parseListLine(currentLine);
        if (currentListLine !== undefined) {
          if (currentListLine.indent === listLine.indent) {
            break;
          }
          if (currentListLine.indent > listLine.indent) {
            const nestedResult = SolContentMarkdownTools.parseList(lines, index, nextId, options, features);
            childList = nestedResult.block;
            index = nestedResult.nextIndex;
            continue;
          }
          break;
        }

        if (SolContentMarkdownTools.getIndent(currentLine) > listLine.indent) {
          itemLines.push(currentLine.trim());
          index++;
          continue;
        }

        break;
      }

      items.push({
        id: nextId('list-item'),
        segments: SolContentMarkdownTools.parseInline(itemLines.join(' '), nextId, options, features),
        childList,
      });
    }

    return {
      block: {
        id: nextId('list'),
        type: 'list',
        ordered: firstLine.ordered,
        items,
      },
      nextIndex: index,
    };
  }

  private static parseQuote(
    lines: string[],
    startIndex: number,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
    features: SolMarkdownFeatures,
  ): BlockParseResult<SolContentQuoteBlock> {
    const quoteLines: string[] = [];
    let index = startIndex;
    while (index < lines.length && SolContentMarkdownTools.isQuoteLine(lines[index])) {
      quoteLines.push(lines[index].replace(/^\s*>\s?/, '').trim());
      index++;
    }

    return {
      block: {
        id: nextId('quote'),
        type: 'quote',
        segments: SolContentMarkdownTools.parseInline(quoteLines.join(' '), nextId, options, features),
      },
      nextIndex: index,
    };
  }

  private static parseParagraph(
    lines: string[],
    startIndex: number,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
    features: SolMarkdownFeatures,
  ): BlockParseResult<SolContentParagraphBlock | SolContentImageBlock> {
    const paragraphLines: string[] = [];
    let index = startIndex;

    while (index < lines.length) {
      const line = lines[index];
      if (line.trim() === '') {
        break;
      }
      if (index !== startIndex && SolContentMarkdownTools.startsNewBlock(lines, index, features)) {
        break;
      }
      paragraphLines.push(line.trim());
      index++;
    }

    const segments = SolContentMarkdownTools.parseInline(paragraphLines.join(' '), nextId, options, features);
    if (segments.length === 1 && segments[0].type === 'image') {
      return {
        block: {
          id: nextId('image-block'),
          type: 'image',
          image: segments[0].image,
          title: segments[0].title,
        },
        nextIndex: index,
      };
    }

    return {
      block: {
        id: nextId('paragraph'),
        type: 'paragraph',
        segments,
      },
      nextIndex: index,
    };
  }

  private static parseInline(
    text: string,
    nextId: (prefix: string) => string,
    options: SolContentMarkdownOptions,
    features: SolMarkdownFeatures,
  ): SolContentInline[] {
    const result: SolContentInline[] = [];
    let cursor = 0;
    let plainText = '';

    const flushPlainText = (): void => {
      if (plainText === '') {
        return;
      }
      result.push({
        id: nextId('text'),
        type: 'text',
        text: plainText,
      } satisfies SolContentTextInline);
      plainText = '';
    };

    while (cursor < text.length) {
      if (text[cursor] === '\\' && /[!-/:-@\[-`{-~]/.test(text[cursor + 1] ?? '')) {
        plainText += text[cursor + 1];
        cursor += 2;
        continue;
      }
      if (features.images && text.startsWith('![', cursor)) {
        const imageToken = SolContentMarkdownTools.parseLinkToken(text, cursor, true);
        if (imageToken !== undefined) {
          flushPlainText();
          const resolvedImage =
            options.imageResolver?.(imageToken.target, imageToken.label, imageToken.title) ??
            SolImage.of(SolContentMarkdownTools.resolveAssetPath(imageToken.target, options.assetBasePath))
              .withAlt(imageToken.label)
              .withObjectFit('contain');
          result.push({
            id: nextId('image'),
            type: 'image',
            image: resolvedImage,
            title: imageToken.title,
          } satisfies SolContentImageInline);
          cursor = imageToken.nextIndex;
          continue;
        }
      }

      if (features.links && text.startsWith('[', cursor)) {
        const linkToken = SolContentMarkdownTools.parseLinkToken(text, cursor, false);
        if (linkToken !== undefined) {
          flushPlainText();
          const target =
            options.linkResolver?.(linkToken.target) ??
            SolContentMarkdownTools.toLinkTarget(linkToken.target, options.assetBasePath);
          result.push({
            id: nextId('link'),
            type: 'link',
            segments: SolContentMarkdownTools.parseInline(linkToken.label, nextId, options, features),
            target,
          } satisfies SolContentLinkInline);
          cursor = linkToken.nextIndex;
          continue;
        }
      }

      if (features.bold && text.startsWith('**', cursor)) {
        const endIndex = SolContentMarkdownTools.findUnescaped(text, '**', cursor + 2);
        if (endIndex > cursor + 2) {
          flushPlainText();
          result.push({
            id: nextId('strong'),
            type: 'strong',
            segments: SolContentMarkdownTools.parseInline(text.slice(cursor + 2, endIndex), nextId, options, features),
          } satisfies SolContentStrongInline);
          cursor = endIndex + 2;
          continue;
        }
      }

      if (features.emphasis && (text.startsWith('*', cursor) || text.startsWith('_', cursor))) {
        const delimiter = text[cursor];
        const endIndex =
          delimiter === '_'
            ? SolContentMarkdownTools.findUnderscoreEmphasisEnd(text, cursor)
            : SolContentMarkdownTools.findUnescaped(text, delimiter, cursor + 1);
        if (endIndex > cursor + 1) {
          flushPlainText();
          result.push({
            id: nextId('emphasis'),
            type: 'emphasis',
            segments: SolContentMarkdownTools.parseInline(text.slice(cursor + 1, endIndex), nextId, options, features),
          } satisfies SolContentEmphasisInline);
          cursor = endIndex + 1;
          continue;
        }
      }

      if (features.inlineCode && text.startsWith('`', cursor)) {
        const endIndex = text.indexOf('`', cursor + 1);
        if (endIndex > cursor + 1) {
          flushPlainText();
          result.push({
            id: nextId('code-inline'),
            type: 'code',
            text: text.slice(cursor + 1, endIndex),
          });
          cursor = endIndex + 1;
          continue;
        }
      }

      plainText += text[cursor];
      cursor++;
    }

    flushPlainText();
    return result;
  }

  /**
   * An underscore inside a word (`merge_requests`) or doubled (`__init__`) never opens or closes emphasis, and
   * emphasis hugs its text, as in CommonMark.
   */
  private static findUnderscoreEmphasisEnd(text: string, start: number): number {
    if (/[\p{L}\p{N}_]/u.test(text[start - 1] ?? ' ') || /[\s_]/u.test(text[start + 1] ?? ' ')) {
      return -1;
    }
    let index = SolContentMarkdownTools.findUnescaped(text, '_', start + 1);
    while (index !== -1 && (/[\p{L}\p{N}_]/u.test(text[index + 1] ?? ' ') || /[\s_]/u.test(text[index - 1]))) {
      index = SolContentMarkdownTools.findUnescaped(text, '_', index + 1);
    }
    return index;
  }

  private static findUnescaped(text: string, delimiter: string, start: number): number {
    for (let index = start; index < text.length; index++) {
      if (text[index] === '\\') {
        index++;
      } else if (text.startsWith(delimiter, index)) {
        return index;
      }
    }
    return -1;
  }

  private static toLinkTarget(target: string, assetBasePath?: string): SolContentLinkTarget {
    if (target.startsWith('#')) {
      return {
        type: 'anchor',
        anchorId: target.slice(1),
      };
    }

    const targetParts = SolContentMarkdownTools.splitLinkTarget(target);
    if (SolContentMarkdownTools.isExternalUrl(targetParts.path)) {
      return {
        type: 'url',
        url: target,
      };
    }

    if (targetParts.path.startsWith('/') && !SolContentMarkdownTools.hasFileExtension(targetParts.path)) {
      return {
        type: 'route',
        routeId: targetParts.path.replace(/^\/+/, ''),
        fragment: targetParts.fragment,
      };
    }

    return {
      type: 'url',
      url: SolContentMarkdownTools.resolveAssetPath(target, assetBasePath),
    };
  }

  private static resolveAssetPath(target: string, assetBasePath?: string): string {
    if (
      target === '' ||
      target.startsWith('/') ||
      target.startsWith('#') ||
      SolContentMarkdownTools.isExternalUrl(target) ||
      target.startsWith('data:')
    ) {
      return target;
    }

    if (assetBasePath === undefined || assetBasePath === '') {
      return target;
    }

    return SolContentMarkdownTools.joinPosix(assetBasePath, target);
  }

  private static splitLinkTarget(target: string): LinkTargetParts {
    const hashIndex = target.indexOf('#');
    if (hashIndex === -1) {
      return {
        path: target,
        fragment: undefined,
      };
    }
    return {
      path: target.slice(0, hashIndex),
      fragment: target.slice(hashIndex + 1) || undefined,
    };
  }

  private static parseLinkToken(
    text: string,
    startIndex: number,
    image: boolean,
  ):
    | {
        readonly label: string;
        readonly target: string;
        readonly title: string | undefined;
        readonly nextIndex: number;
      }
    | undefined {
    const labelStart = image ? startIndex + 2 : startIndex + 1;
    const labelEnd = SolContentMarkdownTools.findUnescaped(text, ']', labelStart);
    if (labelEnd === -1 || text[labelEnd + 1] !== '(') {
      return undefined;
    }

    const targetEnd = text.indexOf(')', labelEnd + 2);
    if (targetEnd === -1) {
      return undefined;
    }

    const rawTarget = text.slice(labelEnd + 2, targetEnd).trim();
    const titleMatch = rawTarget.match(/^(\S+)\s+"([^"]+)"$/);
    const target = titleMatch?.[1] ?? rawTarget;
    const title = titleMatch?.[2];
    return {
      label: text.slice(labelStart, labelEnd),
      target,
      title,
      nextIndex: targetEnd + 1,
    };
  }

  private static isTableStart(lines: string[], index: number): boolean {
    return lines[index].includes('|') && SolContentMarkdownTools.isTableDividerLine(lines[index + 1] ?? '');
  }

  private static parseTableRow(line: string): string[] {
    return line
      .trim()
      .replace(/^\|/, '')
      .replace(/\|$/, '')
      .split('|')
      .map(cell => cell.trim());
  }

  private static isTableDividerLine(line: string): boolean {
    return /^\s*\|?(\s*:?-{3,}:?\s*\|)+\s*:?-{3,}:?\s*\|?\s*$/.test(line);
  }

  private static isFencedCodeBlockStart(line: string): boolean {
    return line.trim().startsWith('```');
  }

  private static isHeadingLine(line: string): boolean {
    return /^\s{0,3}#{1,6}\s+/.test(line);
  }

  private static isDividerLine(line: string): boolean {
    return /^\s*([-*_])(?:\s*\1){2,}\s*$/.test(line);
  }

  private static isQuoteLine(line: string): boolean {
    return /^\s*>\s?/.test(line);
  }

  private static parseListLine(line: string): ListLine | undefined {
    const match = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (!match) {
      return undefined;
    }

    return {
      indent: match[1].length,
      ordered: /\d+\./.test(match[2]),
      content: match[3].trim(),
    };
  }

  private static startsNewBlock(lines: string[], index: number, features: SolMarkdownFeatures): boolean {
    const line = lines[index];
    return (
      (features.codeBlocks && SolContentMarkdownTools.isFencedCodeBlockStart(line)) ||
      (features.headings && SolContentMarkdownTools.isHeadingLine(line)) ||
      (features.dividers && SolContentMarkdownTools.isDividerLine(line)) ||
      (features.quotes && SolContentMarkdownTools.isQuoteLine(line)) ||
      (features.tables && SolContentMarkdownTools.isTableStart(lines, index)) ||
      (features.lists && SolContentMarkdownTools.parseListLine(line) !== undefined)
    );
  }

  private static hasFileExtension(path: string): boolean {
    return /\/?[^/]+\.[a-z0-9]+$/i.test(path);
  }

  private static isExternalUrl(target: string): boolean {
    return /^[a-z][a-z0-9+.-]*:/i.test(target) || target.startsWith('//');
  }

  private static joinPosix(basePath: string, target: string): string {
    const baseSegments = basePath.split('/').filter(Boolean);
    const targetSegments = target.split('/');
    const segments = [...baseSegments];

    for (const segment of targetSegments) {
      if (segment === '' || segment === '.') {
        continue;
      }
      if (segment === '..') {
        segments.pop();
        continue;
      }
      segments.push(segment);
    }

    return segments.join('/');
  }

  private static getIndent(line: string): number {
    return line.match(/^\s*/)?.[0].length ?? 0;
  }

  private static createIdGenerator(): (prefix: string) => string {
    let counter = 0;
    return (prefix: string): string => {
      counter++;
      return `${prefix}-${counter}`;
    };
  }
}
